import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { dateFmt } from "@/lib/format";
import { PageLoader, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Loader2 } from "lucide-react";

export default function Tickets() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-tickets"],
    queryFn: async () => (await api.get("/admin/tickets")).data,
  });
  const [responses, setResponses] = useState({});

  const update = useMutation({
    mutationFn: async ({ id, status, admin_response }) => (await api.put(`/admin/tickets/${id}`, { status, admin_response })).data,
    onSuccess: () => {
      toast.success("Ticket updated");
      qc.invalidateQueries({ queryKey: ["admin-tickets"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Support & discrepancies</p>
        <p className="text-sm text-muted-foreground mt-1">Concerns and discrepancies raised by authors.</p>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : data.length === 0 ? (
        <EmptyState icon={LifeBuoy} title="No tickets raised" desc="Author concerns and discrepancy reports will appear here." />
      ) : (
        <div className="space-y-4">
          {data.map((t) => (
            <Card key={t.id} data-testid={`ticket-${t.id}`} className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {t.ticket_no && <Badge variant="outline" className="font-mono text-xs" data-testid={`ticket-no-${t.id}`}>{t.ticket_no}</Badge>}
                    <p className="font-head font-semibold">{t.subject}</p>
                    <Badge variant="secondary" className="capitalize font-normal">{t.category}</Badge>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t.author_name} · @{t.author_user_id} · {dateFmt(t.created_at)}</p>
                </div>
              </div>
              <p className="text-sm mt-3 text-foreground/90 leading-relaxed whitespace-pre-wrap">{t.message}</p>

              <div className="mt-4 pt-4 border-t border-border space-y-3">
                <Textarea
                  data-testid={`ticket-response-${t.id}`}
                  placeholder="Write a response to the author..."
                  defaultValue={t.admin_response || ""}
                  onChange={(e) => setResponses({ ...responses, [t.id]: e.target.value })}
                  className="min-h-[70px]"
                />
                <div className="flex items-center gap-2 justify-end">
                  <Select defaultValue={t.status} onValueChange={(v) => update.mutate({ id: t.id, status: v, admin_response: responses[t.id] ?? t.admin_response })}>
                    <SelectTrigger className="h-9 w-[150px]" data-testid={`ticket-status-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    data-testid={`save-ticket-${t.id}`}
                    onClick={() => update.mutate({ id: t.id, status: t.status, admin_response: responses[t.id] ?? t.admin_response })}
                    disabled={update.isPending}
                  >
                    {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save response"}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
